import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";

import {
  getProposal,
  getProposalHistory,
  getProposalDocument,
  getProposalTimeline,
  resendProposal,
  getProposalSenders,
  type ProposalSender,
  regenerateProposal,
  deleteProposal,
  setProposalPassword,
  type ProposalEvent,
} from "api/Proposals";

import { getMoProposalContacts } from "api/MaintenanceOpportunities";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import MessageEditor from "components/forms/MessageEditor";
import LeadAttachBar from "./LeadAttachBar";
import SendToContactButton from "./SendToContactButton";
import OutcomeBar from "./OutcomeBar";
import { messageToHtml } from "helpers/messageFormat";

/**
 * A single proposal after it exists.
 *
 * Three questions this answers, in order: what did we actually quote, has
 * anyone opened it, and which sites are suspect. The per-site table is here
 * rather than on the document because it carries provider cost and depot
 * identity — operator-only, and never in the payload the prospect can reach.
 */
const ProposalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState<any>(null);
  const [history, setHistory] = useState<any>({ versions: [], views: [], feedback: [] });
  const [documentHtml, setDocumentHtml] = useState<string | null>(null);
  const [documentReason, setDocumentReason] = useState<string | null>(null);
  const [showDocument, setShowDocument] = useState(false);
  const [events, setEvents] = useState<ProposalEvent[]>([]);
  const [resendTo, setResendTo] = useState("");
  // Full email panel, collapsed by default: subject, message, whose name it
  // goes out in, whether the password rides along. The old single field
  // sent a bare link with no message.
  const [emailOpen, setEmailOpen] = useState(false);
  const [resendSubject, setResendSubject] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [resendFrom, setResendFrom] = useState("");
  const [resendIncludePassword, setResendIncludePassword] = useState(true);
  const [senders, setSenders] = useState<ProposalSender[]>([]);
  useEffect(() => {
    getProposalSenders().then(setSenders);
  }, []);
  const [resending, setResending] = useState(false);
  // Everyone found on the sites this proposal covers (one or a combined set).
  const [siteContacts, setSiteContacts] = useState<Array<{ email: string; contact: string | null; title: string | null; site: string | null }>>([]);
  useEffect(() => {
    if (!id) return;
    getMoProposalContacts(id).then((r: any) => setSiteContacts(r?.contacts ?? []));
  }, [id]);
  const toList = resendTo.split(/[,;\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
  const toggleTo = (email: string) =>
    setResendTo((cur) => {
      const list = cur.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
      const has = list.some((x) => x.toLowerCase() === email);
      return (has ? list.filter((x) => x.toLowerCase() !== email) : [...list, email]).join(", ");
    });
  // Link created but never emailed, on a lead: the lead's contact is who it
  // is for, so "Email this proposal" opens addressed to them (2026-09-28).
  const prefilledTo = React.useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordDraft, setPasswordDraft] = useState<string | null>(null);
  // Click-to-copy for the link and the password: both are for pasting
  // somewhere else, and select-then-copy on a long token is fiddly.
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      setError("Could not copy \u2014 select it and copy by hand.");
    }
  };
  const CopyValue = ({ label, value }: { label: string; value: string }) => (
    <button
      type="button"
      title="Click to copy"
      onClick={() => copy(label, value)}
      style={{
        fontFamily: "inherit",
        fontSize: "inherit",
        background: "#f4f4f1",
        border: "1px solid #e3e3de",
        borderRadius: 5,
        padding: "2px 8px",
        cursor: "pointer",
        color: "#1a1a1a",
      }}
    >
      <code style={{ background: "transparent", padding: 0 }}>{value}</code>
      <span style={{ marginLeft: 8, fontSize: 11, color: copied === label ? "#3b4823" : "#6b6b6b" }}>
        {copied === label ? "Copied" : "Copy"}
      </span>
    </button>
  );
  // The locations table is internal detail (cost, depot, margin). Collapsed by
  // default and paged: 258 rows under the document buried the activity and
  // versions, which are what this screen is for.
  const [showLocations, setShowLocations] = useState(false);
  const [locPage, setLocPage] = useState(0);
  const LOC_PER_PAGE = 25;

  const savePassword = async (value: string | null) => {
    if (!id) return;
    const res: any = await setProposalPassword(id, value);
    if (res?.error) return setError(res.error);
    setProposal((p: any) => ({ ...p, passwordHint: res.password ?? null }));
    setPasswordDraft(null);
  };

  useEffect(() => {
    if (!id) return;
    Promise.all([getProposal(id), getProposalHistory(id), getProposalTimeline(id)]).then(
      ([res, hist, tl]: any[]) => {
        setLoading(false);
        if (res?.error) return setError(res.error);
        setProposal(res);
        if (!prefilledTo.current && !res.sentTo && res.client?.stage === "lead" && res.client?.contactEmail) {
          prefilledTo.current = true;
          setResendTo(res.client.contactEmail);
          const first = String(res.client.contactName ?? "").trim().split(/\s+/)[0];
          if (first) setResendMessage((cur) => cur || `Hi ${first},\n\n`);
        }
        if (!hist?.error) setHistory(hist);
        if (!tl?.error) setEvents(tl.events ?? []);
        document.title = `${process.env.REACT_APP_NAME} | ${res.prospectName}`;
      },
    );
  }, [id]);

  const reloadTimeline = () => {
    if (!id) return;
    getProposalTimeline(id).then((tl: any) => {
      if (!tl?.error) setEvents(tl.events ?? []);
    });
  };

  /**
   * Send the same document to another person — the one the first contact
   * forwarded it to, or the second name in procurement. Same token on purpose:
   * a second link would split the view history and make "has anyone opened
   * it" unanswerable.
   */
  const resend = async () => {
    if (!id || !toList.length) return;
    setResending(true);
    // One email per person (same link), so nobody sees the others' addresses.
    let res: any = null;
    for (const to of toList) {
      res = await resendProposal(id, {
        to,
        subject: resendSubject || undefined,
        message: resendMessage || undefined,
        includePassword: resendIncludePassword,
        fromUserId: resendFrom || undefined,
      });
      if (res?.error) break;
    }
    setResending(false);
    if (res?.error) return setError(res.error);
    setResendTo("");
    setResendMessage("");
    reloadTimeline();
  };

  const [regenOpen, setRegenOpen] = useState(false);
  const [regenFrom, setRegenFrom] = useState("");
  const regenerate = async () => {
    if (!id) return;
    // A new version is FROM someone. Same document under another name is
    // its own version with its own link and history.
    const res: any = await regenerateProposal(id, regenFrom || null);
    if (res?.error) return setError(res.error);
    navigate(`/proposals/${res.id}`);
  };

  /**
   * Delete, with the consequence stated before the click: a sent proposal's
   * share link dies with it. A won proposal is refused server-side.
   */
  const remove = async () => {
    if (!id || !proposal) return;
    const sent = proposal.status !== "draft";
    const ok = window.confirm(
      sent
        ? `Delete "${proposal.name}"? The link the prospect has will stop working, and this cannot be undone from the app.`
        : `Delete the draft "${proposal.name}"? This cannot be undone from the app.`,
    );
    if (!ok) return;
    const res: any = await deleteProposal(id);
    if (res?.error) return setError(res.error);
    navigate("/proposals");
  };

  /**
   * Read the document in the app rather than through the share link. The
   * prospect's copy sits on a public host that only resolves where DNS points
   * at it, so outside production the link is unreachable — and even in
   * production an operator should not have to know the password to read what
   * was sent.
   */
  const viewDocument = async () => {
    setShowDocument(true);
    if (documentHtml || documentReason || !id) return;
    const res: any = await getProposalDocument(id);
    if (res?.error) return setError(res.error);
    setDocumentHtml(res.html);
    setDocumentReason(res.reason);
  };

  if (loading)
    return (
      <>
        <Header />
        <Page title="Proposal">Loading…</Page>
      </>
    );
  if (error)
    return (
      <>
        <Header />
        <Page title="Proposal">
          <div className={styles.err}>{error}</div>
        </Page>
      </>
    );
  if (!proposal) return null;

  const locations: any[] = proposal.locations ?? [];
  const unpriced = locations.filter((l) => !l.priced);
  const repaired = locations.filter((l) => l.depotCoordinatesRepaired);
  // Built server-side from the environment. Assembling it here against a
  // hardcoded host is how a sandbox proposal ends up linking to production.
  const shareUrl: string | null = proposal.shareUrl ?? null;

  return (
    <>
      <Header />
      <Page
        title={`${proposal.prospectName}${proposal.version > 1 ? ` \u00b7 v${proposal.version}` : ""}`}
        subtitle={
          proposal.status === "draft"
            ? `Draft \u2014 reprices every time it is opened.${
                proposal.sender ? ` From ${`${proposal.sender.firstName ?? ""} ${proposal.sender.lastName ?? ""}`.trim()}.` : ""
              }`
            : `${proposal.sentTo ? "Sent" : "Link created"} ${
                proposal.sentAt ? new Date(proposal.sentAt).toLocaleDateString() : ""
              }${proposal.sentTo ? ` to ${proposal.sentTo}` : ""}${
                proposal.sender ? ` · from ${`${proposal.sender.firstName ?? ""} ${proposal.sender.lastName ?? ""}`.trim()}` : ""
              }. Frozen at the figures that went out.`
        }
        titleAction={
          <>
            <BasicButton outline scheme="secondary" keepText onClick={() => navigate("/proposals")}>
              All proposals
            </BasicButton>
            {proposal.status !== "won" && (
              <BasicButton outline scheme="danger" keepText onClick={remove}>
                Delete
              </BasicButton>
            )}
            {proposal.status === "draft" ? (
              <BasicButton keepText onClick={() => navigate(`/proposals/${proposal.id}/edit`)}>
                Continue this draft
              </BasicButton>
            ) : (
              <>
                <BasicButton outline scheme="secondary" keepText onClick={() => (showDocument ? setShowDocument(false) : viewDocument())}>
                  {showDocument ? "Close the document" : "View the document"}
                </BasicButton>
                <SendToContactButton proposal={proposal} onSent={() => reloadTimeline()} />
                <BasicButton keepText onClick={() => setRegenOpen((v) => !v)}>
                  Regenerate as a new version
                </BasicButton>
              </>
            )}
          </>
        }
      >
        <div className={styles.wrap}>

      <LeadAttachBar
        proposal={proposal}
        onChanged={() => {
          if (!id) return;
          getProposal(id).then((res: any) => { if (!res?.error) setProposal(res); });
          reloadTimeline();
        }}
      />

      {proposal.status !== "draft" && (
        <OutcomeBar
          proposal={proposal}
          onChanged={() => {
            if (!id) return;
            getProposal(id).then((res: any) => { if (!res?.error) setProposal(res); });
            reloadTimeline();
          }}
        />
      )}

      {showDocument && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 8 }}>
            <h2 style={{ fontSize: 14, margin: 0 }}>The document as it was sent</h2>
            <button className={`${styles.btn} ${styles.ghost}`} style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => setShowDocument(false)}>
              Close
            </button>
          </div>
          <p className={styles.sub}>
            Rendered from the frozen payload, so this is exactly what the prospect opened — not a
            re-price.
          </p>
          {documentReason ? (
            <div className={styles.note}>{documentReason}</div>
          ) : documentHtml ? (
            <iframe className={styles.previewFrame} title="Proposal document" srcDoc={documentHtml} />
          ) : (
            <p className={styles.sub}>Loading…</p>
          )}
        </>
      )}

      {regenOpen && (
        <div className={styles.note} style={{ marginBottom: 18 }}>
          <b>New version</b>
          <div className={styles.sub} style={{ margin: "2px 0 10px" }}>
            Reprices against today&apos;s rate card and gets its own link and history. This version stays as it is.
          </div>
          <div className={styles.row} style={{ alignItems: "flex-end" }}>
            <div className={styles.field} style={{ maxWidth: 360 }}>
              <label>From</label>
              <select value={regenFrom} onChange={(e) => setRegenFrom(e.target.value)}>
                <option value="">
                  Keep {proposal.sender ? `${proposal.sender.firstName ?? ""} ${proposal.sender.lastName ?? ""}`.trim() : "the current sender"}
                </option>
                {senders.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.jobTitle ? ` · ${s.jobTitle}` : ""}
                  </option>
                ))}
              </select>
              <span className={styles.sub}>Their name, title, email and phone sign the new document.</span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className={styles.btn} onClick={regenerate}>Create new version</button>
              <button className={`${styles.btn} ${styles.ghost}`} onClick={() => setRegenOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {shareUrl && (
        <div className={styles.note}>
          <b>Share link:</b>{" "}<CopyValue label="link" value={shareUrl} />
          <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span>
              <b>Password:</b>{" "}
              {proposal.passwordHint ? <CopyValue label="password" value={proposal.passwordHint} /> : <i>none — the link alone opens it</i>}
            </span>
            {passwordDraft === null ? (
              <>
                <button className={`${styles.btn} ${styles.ghost}`} style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => setPasswordDraft(proposal.passwordHint ?? "")}>
                  {proposal.passwordHint ? "Change" : "Add one"}
                </button>
                {proposal.passwordHint && (
                  <button className={`${styles.btn} ${styles.ghost}`} style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => savePassword(null)}>
                    Remove
                  </button>
                )}
              </>
            ) : (
              <>
                <input style={{ width: 200 }} value={passwordDraft} onChange={(e) => setPasswordDraft(e.target.value)} />
                <button className={styles.btn} style={{ padding: "3px 10px", fontSize: 11 }} disabled={passwordDraft.trim().length < 4} onClick={() => savePassword(passwordDraft.trim())}>
                  Save
                </button>
                <button className={`${styles.btn} ${styles.ghost}`} style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => setPasswordDraft(null)}>
                  Cancel
                </button>
              </>
            )}
          </div>
          <div className={styles.sub} style={{ marginTop: 4 }}>Changing or removing the password does not change the link.</div>
        </div>
      )}

      {proposal.status !== "draft" && (
        <div className={styles.row} style={{ marginTop: 18 }}>
          <Stat label="Views" value={proposal.viewCount || 0} />
          <Stat
            label="First opened"
            value={
              proposal.firstViewedAt
                ? new Date(proposal.firstViewedAt).toLocaleDateString()
                : "Never"
            }
          />
          <Stat
            label="Last opened"
            value={
              proposal.lastViewedAt ? new Date(proposal.lastViewedAt).toLocaleDateString() : "—"
            }
          />
        </div>
      )}

      {(unpriced.length > 0 || repaired.length > 0) && (
        <div className={styles.note}>
          {unpriced.length > 0 && (
            <div>
              <b>{unpriced.length} locations were not priced</b> and are excluded from every figure
              in the document.
            </div>
          )}
          {repaired.length > 0 && (
            <div style={{ marginTop: unpriced.length > 0 ? 8 : 0 }}>
              <b>{repaired.length} priced off a repaired depot coordinate.</b> Those depots have
              latitude and longitude stored the wrong way round; the rate is corrected at read
              time, the underlying data is not.
            </div>
          )}
        </div>
      )}

      {history.versions.length > 1 && (
        <>
          <h2 style={{ fontSize: 14, marginTop: 24 }}>Versions</h2>
          <p className={styles.sub}>
            A sent proposal never changes. Revising one produces a new version, so what the
            prospect already read keeps saying what it said.
          </p>
          <table>
            <thead>
              <tr>
                <th>Version</th>
                <th>From</th>
                <th>Sent to</th>
                <th>Status</th>
                <th>Sent</th>
                <th>Opened</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {history.versions.map((v: any) => (
                <tr key={v.id} style={{ fontWeight: v.id === proposal.id ? 700 : 400 }}>
                  <td>v{v.version}</td>
                  <td>{v.fromName || "—"}</td>
                  <td>{v.sentTo || "-"}</td>
                  <td>{v.status}</td>
                  <td>{v.sentAt ? new Date(v.sentAt).toLocaleDateString() : "—"}</td>
                  <td>{v.viewCount || "never"}</td>
                  <td style={{ textAlign: "right" }}>
                    {v.id !== proposal.id && (
                      <button
                        className={`${styles.btn} ${styles.ghost}`}
                        style={{ padding: "4px 10px", fontSize: 11 }}
                        onClick={() => navigate(`/proposals/${v.id}`)}
                      >
                        Open
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h2 style={{ fontSize: 14, marginTop: 24 }}>Activity</h2>
      <p className={styles.sub}>
        Everything that has happened to this proposal, newest first — who sent it, to whom, every
        open, and anything the recipient said back.
      </p>

      {proposal.status !== "draft" && (
        <div className={styles.note} style={{ marginTop: 0, marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <b>Email this proposal</b>
              <div className={styles.sub} style={{ margin: 0 }}>
                Same document, same link. A second link would split the open history in half.
              </div>
            </div>
            <button className={`${styles.btn} ${styles.ghost}`} style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => setEmailOpen((v) => !v)}>
              {emailOpen ? "Close" : "Compose"}
            </button>
          </div>
          {emailOpen && (
            <div style={{ marginTop: 12 }}>
              <div className={styles.row} style={{ marginTop: 0 }}>
                <div className={styles.field}>
                  <label>To</label>
                  <input value={resendTo} onChange={(e) => setResendTo(e.target.value)} placeholder="someone-else@… (several: separate with commas)" />
                  {siteContacts.length > 0 && (
                    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                      <span className={styles.sub}>Found on {siteContacts.length === 1 ? "this site" : "these sites"} - tick who gets it:</span>
                      {siteContacts.map((c) => (
                        <label key={c.email} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, textTransform: "none", letterSpacing: 0, fontWeight: 500, color: "#1a1a1a" }}>
                          <input type="checkbox" style={{ width: 14, height: 14, margin: 0 }} checked={toList.includes(c.email)} onChange={() => toggleTo(c.email)} />
                          {c.contact || c.email}{c.title ? ` · ${c.title}` : ""} <span style={{ color: "#888" }}>{c.contact ? c.email : ""}{c.site ? ` · ${c.site}` : ""}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Send as</label>
                  <select value={resendFrom} onChange={(e) => setResendFrom(e.target.value)}>
                    <option value="">The person on the document (default)</option>
                    {senders.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}{s.jobTitle ? ` · ${s.jobTitle}` : ""}
                      </option>
                    ))}
                  </select>
                  <span className={styles.sub}>
                    Sent from this person&apos;s own mailbox (and saved to their Sent Items) when Microsoft 365 allows it; otherwise from the system address with replies going to them. The document keeps the signature it was created with; for a different name on the document, regenerate as a new version.
                  </span>
                </div>
              </div>
              <div className={styles.field}>
                <label>Subject</label>
                <input value={resendSubject} onChange={(e) => setResendSubject(e.target.value)} placeholder={`${proposal.prospectName} — preliminary pricing`} />
              </div>
              <div className={styles.field}>
                <label>Message</label>
                <MessageEditor
                  value={resendMessage}
                  onChange={setResendMessage}
                  placeholder="A View proposal button sits above this, and the link repeats below it."
                  email={{
                    shareUrl: proposal.shareUrl ?? null,
                    password: proposal.passwordHint ?? null,
                    hasPassword: !!proposal.passwordHint,
                    includePassword: resendIncludePassword,
                    fromName: (resendFrom ? senders.find((s) => s.id === resendFrom)?.name : null) || proposal.payload?.contact?.name || "GetMulch.com",
                    replyTo: (resendFrom ? senders.find((s) => s.id === resendFrom)?.email : null) || proposal.payload?.contact?.email || null,
                    subject: resendSubject || `${proposal.prospectName} \u2014 preliminary pricing`,
                    to: toList[0] || null,
                  }}
                />
              </div>
              {proposal.passwordHint && (
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 10, textTransform: "none", letterSpacing: 0, fontWeight: 500, color: "#1a1a1a" }}>
                  <input type="checkbox" style={{ width: 16, height: 16, margin: 0 }} checked={resendIncludePassword} onChange={(e) => setResendIncludePassword(e.target.checked)} />
                  Include the password in the email
                </label>
              )}
              <button className={styles.btn} onClick={resend} disabled={resending || !toList.length}>
                {resending ? "Sending…" : toList.length > 1 ? `Send to ${toList.length}` : "Send"}
              </button>
            </div>
          )}
        </div>
      )}

      {events.length === 0 ? (
        <p className={styles.sub}>Nothing recorded yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th style={{ width: 180 }}>When</th>
              <th>What</th>
              <th>Detail</th>
              <th style={{ width: 160 }}>Who</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e, i) => (
              <tr key={i}>
                <td style={{ whiteSpace: "nowrap" }}>{new Date(e.at).toLocaleString()}</td>
                <td>
                  <span
                    className={`${styles.pill} ${
                      e.kind === "replied"
                        ? styles.ok
                        : e.kind === "opened"
                        ? styles.info
                        : e.kind === "regenerated"
                        ? styles.warn
                        : styles.info
                    }`}
                  >
                    {e.kind}
                  </span>{" "}
                  {e.summary}
                </td>
                <td style={{ fontSize: 11, color: "#6b7787" }}>
                  {e.detail ?? "—"}
                  {e.message && (
                    <div
                      style={{ marginTop: 6, padding: "8px 10px", background: "#F7F8FA", borderLeft: "3px solid #C9D3E0", borderRadius: 4, color: "#2b3340", fontSize: 12 }}
                      // Same formatter as the email; escapes before adding tags.
                      dangerouslySetInnerHTML={{ __html: messageToHtml(e.message) }}
                    />
                  )}
                </td>
                <td style={{ fontSize: 11, color: "#6b7787" }}>{e.actor ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 24 }}>
        <h2 style={{ fontSize: 14, margin: 0 }}>Locations · {locations.length}</h2>
        <button className={`${styles.btn} ${styles.ghost}`} style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => setShowLocations((v) => !v)}>
          {showLocations ? "Hide" : "Show"}
        </button>
      </div>
      {showLocations && (
      <>
      <p className={styles.sub}>
        Cost and depot are internal. They are on this screen and never in the document.
      </p>
      <table>
        <thead>
          <tr>
            <th>Site</th>
            <th>State</th>
            <th>Yards</th>
            <th>Depot</th>
            <th>Cost/yd</th>
            <th>Price/yd</th>
            <th>Margin</th>
          </tr>
        </thead>
        <tbody>
          {locations.slice(locPage * LOC_PER_PAGE, (locPage + 1) * LOC_PER_PAGE).map((l) => {
            const cost = Number(l.cost ?? 0);
            const price = Number(l.pricePerYard ?? 0);
            const margin = price > 0 ? ((price - cost) / price) * 100 : null;
            return (
              <tr key={l.id} className={!l.priced ? styles.flagged : undefined}>
                <td>{l.name ?? "—"}</td>
                <td>{l.state ?? "—"}</td>
                <td>{l.yards ? Math.round(Number(l.yards)) : "—"}</td>
                <td>
                  {l.depotName ?? (
                    <span className={`${styles.pill} ${styles.bad}`}>{l.unpricedReason}</span>
                  )}
                  {l.depotCoordinatesRepaired && (
                    <span className={`${styles.pill} ${styles.warn}`} style={{ marginLeft: 6 }}>
                      repaired
                    </span>
                  )}
                </td>
                <td>{l.cost ? `$${cost.toFixed(2)}` : "—"}</td>
                <td>{l.pricePerYard ? `$${price.toFixed(2)}` : "—"}</td>
                <td>{margin != null ? `${margin.toFixed(0)}%` : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {locations.length > LOC_PER_PAGE && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
          <button className={`${styles.btn} ${styles.ghost}`} disabled={locPage === 0} onClick={() => setLocPage((p) => Math.max(p - 1, 0))}>
            Previous
          </button>
          <span className={styles.sub} style={{ margin: 0 }}>
            {locPage * LOC_PER_PAGE + 1}–{Math.min((locPage + 1) * LOC_PER_PAGE, locations.length)} of {locations.length}
          </span>
          <button className={`${styles.btn} ${styles.ghost}`} disabled={(locPage + 1) * LOC_PER_PAGE >= locations.length} onClick={() => setLocPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}
      </>
      )}
        </div>
      </Page>
    </>
  );
};

const Stat = ({ label, value }: { label: string; value: any }) => (
  <div className={styles.field} style={{ maxWidth: 180 }}>
    <label>{label}</label>
    <div style={{ fontSize: 22, fontWeight: 700, color: "#2b3340" }}>{value}</div>
  </div>
);

export default ProposalDetail;
